
import { useAuth0 } from '@auth0/auth0-react';
import { isEmptyObject } from 'functions/isEmptyObject';
import { HttpMethod } from 'models/HttpMethod';
import { LogLevel } from 'models/LogLevel';

import Identifiable from '../interfaces/Identifiable';
import { ApiService } from '../services/ApiService';


export const useModifyEntityService = <T extends Identifiable>(
  apiBaseUrl: string,
  apiEndpoint: string,
  sendLog: (message: string, logLevel: LogLevel, data: object) => void,
  onTokenExpired: () => void,
) => {
  const { getAccessTokenSilently } = useAuth0();
  const scheduleService = new ApiService<T>(apiBaseUrl, apiEndpoint);

  const createEntity = async (entity: T, queryParams: URLSearchParams | undefined = undefined): Promise<T | void> => {
    try {
      if (isEmptyObject(entity)) {
        return await Promise.resolve();
      }
      const token = await getAccessTokenSilently();
      if (!token) {
        onTokenExpired();
        return await Promise.reject();
      }
      scheduleService.setAccessToken(token);
      return await scheduleService.create(entity, queryParams);
    } catch (e: any) {
      sendLog(e.message, LogLevel.error, e);
      return await Promise.reject();
    }
  };

  const updateEntity = async (entity: T, queryParams: URLSearchParams | undefined = undefined): Promise<boolean> => {
    try {
      if (isEmptyObject(entity)) {
        return false;
      }
      const token = await getAccessTokenSilently();
      if (!token) {
        onTokenExpired();
        return false;
      }
      scheduleService.setAccessToken(token);
      return await scheduleService.update(entity, queryParams);
    } catch (e: any) {
      sendLog(e.message, LogLevel.error, e);
      return false;
    }
  };

  const executeEntityAction = async (
    uriActionPath: string,
    method: HttpMethod,
    data: any,
  ): Promise<Response | void> => {
    try {
      const token = await getAccessTokenSilently();
      if (!token) {
        onTokenExpired();
        return await Promise.reject();
      }
      scheduleService.setAccessToken(token);
      const result = await scheduleService.execute(uriActionPath, method, data);
      return result;
    } catch (e: any) {
      sendLog(e.message, LogLevel.error, e);
      return await Promise.reject();
    }
  };

  const deleteEntity = async (id: number): Promise<boolean> => {
    try {
      const token = await getAccessTokenSilently();
      if (!token) {
        onTokenExpired();
        return false;
      }
      scheduleService.setAccessToken(token);
      return await scheduleService.delete(id);
    } catch (e: any) {
      sendLog(e.message, LogLevel.error, e);
      return false;
    }
  };

  return {
    createEntity,
    updateEntity,
    deleteEntity,
    executeEntityAction,
  };
};
